import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileTextIcon, UserIcon, BuildingIcon, PillIcon, CameraIcon, CheckCircleIcon, ShieldCheckIcon, ArrowRightIcon, ArrowLeftIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export function TransferPrescription() {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const nextStep = () => setStep(s => s + 1);
  const prevStep = () => setStep(s => s - 1);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  if (submitted) {
    return <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} className="min-h-[80vh] flex items-center justify-center px-4" data-id="element-575">
        <div className="max-w-md w-full text-center bg-white p-12 rounded-3xl shadow-2xl border border-slate-100" data-id="element-576">
          <div className="w-24 h-24 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-8" data-id="element-577">
            <CheckCircleIcon className="w-12 h-12" data-id="element-578" />
          </div>
          <h1 className="text-3xl font-black text-slate-900 mb-4 uppercase tracking-tight" data-id="element-579">
            Transfer Request Sent!
          </h1>
          <p className="text-slate-500 mb-10 leading-relaxed" data-id="element-580">
            Thank you for choosing Daily Care Pharmacy. Our staff will contact
            your current pharmacy and handle the transfer for you. We'll notify
            you when your prescriptions are ready.
          </p>
          <Link to="/" className="inline-flex items-center gap-2 bg-clinical-blue text-white font-black py-4 px-8 rounded-xl hover:bg-blue-700 transition-all uppercase tracking-widest text-xs" data-id="element-581">
            Return Home
          </Link>
        </div>
      </motion.div>;
  }
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="bg-slate-50 min-h-screen py-12 md:py-20" data-id="element-582">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-583">
        <div className="mb-12 text-center" data-id="element-584">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-clinical-blue/5 text-clinical-blue rounded-full text-[10px] font-bold uppercase tracking-widest mb-6" data-id="element-585">
            Prescription Services
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-slate-900 mb-4 uppercase tracking-tighter" data-id="element-586">
            Transfer Your Prescription
          </h1>
          <p className="text-slate-600" data-id="element-587">
            Switching is easy. We'll handle the communication with your old
            pharmacy.
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-12 flex justify-between items-center relative" data-id="element-588">
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-slate-200 z-0" data-id="element-589"></div>
          <div className="absolute left-0 top-1/2 -translate-y-1/2 h-1 bg-clinical-blue z-0 transition-all duration-500" style={{
          width: `${(step - 1) / 2 * 100}%`
        }} data-id="element-590"></div>

          {[1, 2, 3].map(s => <div key={s} className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-500 ${step >= s ? 'bg-clinical-blue text-white shadow-lg shadow-clinical-blue/30' : 'bg-white text-slate-400 border-2 border-slate-200'}`} data-id="element-591">
              {step > s ? <CheckCircleIcon className="w-5 h-5" data-id="element-592" /> : s}
            </div>)}
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden" data-id="element-593">
          <div className="p-8 md:p-12" data-id="element-594">
            <AnimatePresence mode="wait" data-id="element-595">
              {step === 1 && <motion.div key="step1" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} className="space-y-6" data-id="element-596">
                  <div className="flex items-center gap-3 mb-8" data-id="element-597">
                    <UserIcon className="w-6 h-6 text-clinical-blue" data-id="element-598" />
                    <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight" data-id="element-599">
                      Patient Information
                    </h2>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6" data-id="element-600">
                    <div className="space-y-2" data-id="element-601">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-602">
                        Full Name
                      </label>
                      <input required type="text" placeholder="John Doe" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 focus:border-clinical-blue outline-none transition-all" data-id="element-603" />
                    </div>
                    <div className="space-y-2" data-id="element-604">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-605">
                        Date of Birth
                      </label>
                      <input required type="date" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 focus:border-clinical-blue outline-none transition-all" data-id="element-606" />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6" data-id="element-607">
                    <div className="space-y-2" data-id="element-608">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-609">
                        Phone Number
                      </label>
                      <input required type="tel" placeholder="(555) 000-0000" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 focus:border-clinical-blue outline-none transition-all" data-id="element-610" />
                    </div>
                    <div className="space-y-2" data-id="element-611">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-612">
                        Email Address
                      </label>
                      <input required type="email" placeholder="john@example.com" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 focus:border-clinical-blue outline-none transition-all" data-id="element-613" />
                    </div>
                  </div>
                </motion.div>}

              {step === 2 && <motion.div key="step2" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} className="space-y-6" data-id="element-614">
                  <div className="flex items-center gap-3 mb-8" data-id="element-615">
                    <BuildingIcon className="w-6 h-6 text-clinical-blue" data-id="element-616" />
                    <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight" data-id="element-617">
                      Current Pharmacy
                    </h2>
                  </div>

                  <div className="space-y-2" data-id="element-618">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-619">
                      Pharmacy Name
                    </label>
                    <input required type="text" placeholder="e.g. CVS, Walgreens, Local Pharmacy" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 focus:border-clinical-blue outline-none transition-all" data-id="element-620" />
                  </div>

                  <div className="space-y-2" data-id="element-621">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-622">
                      Pharmacy Phone Number
                    </label>
                    <input required type="tel" placeholder="(555) 000-0000" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 focus:border-clinical-blue outline-none transition-all" data-id="element-623" />
                  </div>

                  <div className="space-y-2" data-id="element-624">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-625">
                      Pharmacy Address
                    </label>
                    <textarea required rows={3} placeholder="Street, City, State, Zip" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 focus:border-clinical-blue outline-none transition-all resize-none" data-id="element-626"></textarea>
                  </div>
                </motion.div>}

              {step === 3 && <motion.div key="step3" initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} className="space-y-6" data-id="element-627">
                  <div className="flex items-center gap-3 mb-8" data-id="element-628">
                    <PillIcon className="w-6 h-6 text-clinical-blue" data-id="element-629" />
                    <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight" data-id="element-630">
                      Prescription Details
                    </h2>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6" data-id="element-631">
                    <div className="space-y-2" data-id="element-632">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-633">
                        Medication Name
                      </label>
                      <input required type="text" placeholder="e.g. Amoxicillin" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 focus:border-clinical-blue outline-none transition-all" data-id="element-634" />
                    </div>
                    <div className="space-y-2" data-id="element-635">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-636">
                        Rx Number (Optional)
                      </label>
                      <input type="text" placeholder="e.g. 1234567" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 focus:border-clinical-blue outline-none transition-all" data-id="element-637" />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6" data-id="element-638">
                    <div className="space-y-2" data-id="element-639">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-640">
                        Dosage
                      </label>
                      <input required type="text" placeholder="e.g. 500mg" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 focus:border-clinical-blue outline-none transition-all" data-id="element-641" />
                    </div>
                    <div className="space-y-2" data-id="element-642">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-643">
                        Quantity
                      </label>
                      <input required type="text" placeholder="e.g. 30 tablets" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-clinical-blue/20 focus:border-clinical-blue outline-none transition-all" data-id="element-644" />
                    </div>
                  </div>

                  <div className="pt-4" data-id="element-645">
                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-3" data-id="element-646">
                      Upload Prescription Photo (Optional)
                    </label>
                    <div className="border-2 border-dashed border-slate-200 rounded-2xl p-8 text-center hover:border-clinical-blue transition-colors cursor-pointer group" data-id="element-647">
                      <CameraIcon className="w-8 h-8 text-slate-300 mx-auto mb-3 group-hover:text-clinical-blue transition-colors" data-id="element-648" />
                      <p className="text-sm text-slate-500" data-id="element-649">
                        Click to upload or drag and drop
                      </p>
                      <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-widest font-bold" data-id="element-650">
                        JPG, PNG up to 10MB
                      </p>
                    </div>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-xl flex gap-3 items-start" data-id="element-651">
                    <ShieldCheckIcon className="w-5 h-5 text-clinical-blue flex-shrink-0 mt-0.5" data-id="element-652" />
                    <p className="text-[10px] text-slate-600 leading-relaxed" data-id="element-653">
                      <span className="font-bold text-clinical-blue uppercase tracking-widest block mb-1" data-id="element-654">
                        HIPAA Compliance Notice
                      </span>
                      Your information is protected by federal law. By
                      submitting this form, you authorize Daily Care Pharmacy to
                      contact your current pharmacy to facilitate the transfer
                      of your prescriptions.
                    </p>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>

          <div className="px-8 py-6 bg-slate-50 border-t border-slate-100 flex justify-between items-center" data-id="element-655">
            {step > 1 ? <button type="button" onClick={prevStep} className="flex items-center gap-2 text-slate-500 hover:text-slate-900 font-bold uppercase tracking-widest text-[10px] transition-colors" data-id="element-656">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-657" /> Back
              </button> : <div data-id="element-658"></div>}

            {step < 3 ? <button type="button" onClick={nextStep} className="bg-clinical-blue text-white font-black py-3 px-8 rounded-xl hover:bg-blue-700 transition-all uppercase tracking-widest text-[10px] flex items-center gap-2 shadow-lg shadow-clinical-blue/20" data-id="element-659">
                Continue <ArrowRightIcon className="w-4 h-4" data-id="element-660" />
              </button> : <button type="submit" className="bg-clinical-blue text-white font-black py-3 px-8 rounded-xl hover:bg-blue-700 transition-all uppercase tracking-widest text-[10px] flex items-center gap-2 shadow-lg shadow-clinical-blue/20" data-id="element-661">
                Submit Transfer Request
              </button>}
          </div>
        </form>
      </div>
    </motion.div>;
}